/**
 * @description Route orchestration component for the time clock portal
 *
 * Key features:
 * - Shows loading state while fetching company config
 * - Routes unauthenticated users to EmployeeSelect → PinEntry flow
 * - Routes authenticated users to the tabbed portal (Schedule, Timesheet, etc.)
 * - Handles session timeout redirect
 * - Injects SO brand colors from company config branding
 * - Progressive favicon; SO hosts keep Worker /apple-touch-icon.png?v= (never the bare path)
 * - Shows tenant logo in header if available
 * - Pay classification handling:
 *   - Hourly: Full access (Schedule, Timesheet, Time Off, Pay Stubs)
 *   - Salaried: Limited access (Time Off, Pay Stubs only - no Schedule/Timesheet)
 *   - Contractor: Filtered out by backend (won't appear in employee list)
 *
 * @dependencies
 * - ClockContext: Session state
 * - colorUtils: Brand CSS variable injection
 * - Screen components: EmployeeSelect, PinEntry, portal tabs
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useClockContext } from '../contexts/ClockContext';
import { generateBrandVariants, injectBrandCSS } from '../utils/colorUtils';
import EmployeeSelect from './screens/EmployeeSelect';
import PinEntry from './screens/PinEntry';
import ScheduleScreen from './screens/ScheduleScreen';
import TimesheetScreen from './screens/TimesheetScreen';
import TimeOffScreen from './screens/TimeOffScreen';
import TabNavigation from './shared/TabNavigation';
import { preserveSoAppleTouchIcon } from '../utils/soAppleTouch';

const TAB_TITLES = { schedule: 'Schedule', timesheet: 'Timesheet', timeoff: 'Time Off', paystubs: 'Pay Stubs' };

const ClockRouter = () => {
  const { companyConfig, isAuthenticated, loading, error, employee, logout } = useClockContext();
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Determine if employee is salaried (no time clock access)
  const isSalaried = useMemo(() => {
    return employee?.payClassification === 'salaried';
  }, [employee]);

  // Default tab: 'timeoff' for salaried, 'schedule' for hourly
  const defaultTab = isSalaried ? 'timeoff' : 'schedule';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Reset to default tab when employee changes
  useEffect(() => {
    setActiveTab(isSalaried ? 'timeoff' : 'schedule');
  }, [isSalaried]);

  // Inject brand CSS vars when company config loads
  useEffect(() => {
    if (!companyConfig) return;
    const branding = companyConfig.branding;
    if (branding?.colors?.primaryColor && branding?.colors?.secondaryColor) {
      const variants = generateBrandVariants(branding.colors.primaryColor, branding.colors.secondaryColor);
      injectBrandCSS(variants);
    }
  }, [companyConfig]);

  // Progressive tab + home-screen icons from this SO's branding only.
  // Empty CP/SO never inherit LynxPay (Worker first-HTML is SoT on SO hosts).
  useEffect(() => {
    const branding = companyConfig?.branding || {};
    const assets = branding.assets || {};
    const favicon = assets.faviconUrl;
    const mark = assets.logoMarkUrl;
    const raster = (url) => typeof url === 'string' && /\.(png|jpe?g|webp)$/i.test(url.split('?')[0]);
    const setLink = (rel, href, selector) => {
      let link = document.querySelector(selector);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };
    if (favicon) setLink('icon', favicon, "link[rel~='icon']");
    const onSoHost = typeof window !== 'undefined'
      && String(window.location.hostname || '').endsWith('.lynx.build');
    if (onSoHost) {
      preserveSoAppleTouchIcon(document);
    } else {
      const apple = raster(mark) ? mark : (raster(favicon) ? favicon : null);
      if (apple) setLink('apple-touch-icon', apple, "link[rel='apple-touch-icon']");
    }
    const title = branding.identity?.platformName || companyConfig?.companyName;
    if (title && typeof document !== 'undefined') {
      let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'apple-mobile-web-app-title';
        document.head.appendChild(meta);
      }
      meta.content = title;
    }
  }, [companyConfig]);

  // Per-screen document title — format: "ScreenTitle | CompanyName"
  useEffect(() => {
    const companyName = companyConfig?.companyName || 'Employee Portal';
    let pageTitle;
    if (!isAuthenticated) {
      pageTitle = selectedEmployee ? 'PIN Entry' : 'Sign In';
    } else {
      pageTitle = TAB_TITLES[activeTab] || 'Employee Portal';
    }
    document.title = `${pageTitle} | ${companyName}`;
  }, [companyConfig?.companyName, isAuthenticated, selectedEmployee, activeTab]);

  // Tabs configuration - must be before early returns (Rules of Hooks)
  // Salaried employees only see Time Off and Pay Stubs (no Schedule/Timesheet)
  const tabs = useMemo(() => {
    if (isSalaried) {
      return [
        { key: 'timeoff', label: 'Time Off' },
        { key: 'paystubs', label: 'Pay Stubs', disabled: true },
      ];
    }
    return [
      { key: 'schedule', label: 'Schedule' },
      { key: 'timesheet', label: 'Timesheet' },
      { key: 'timeoff', label: 'Time Off' },
      { key: 'paystubs', label: 'Pay Stubs', disabled: true },
    ];
  }, [isSalaried]);

  // Extract logo from branding config
  const logoMarkUrl = companyConfig?.branding?.assets?.logoMarkUrl;
  const platformName = companyConfig?.branding?.identity?.platformName;

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !companyConfig) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Employee Portal</h1>
          <p className="text-red-400">{error}</p>
          <p className="text-slate-500 text-sm mt-2">Please check the link and try again.</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show employee select or PIN entry
  if (!isAuthenticated) {
    if (!selectedEmployee) {
      return (
        <EmployeeSelect
          companyName={companyConfig?.companyName}
          onSelect={(emp) => setSelectedEmployee(emp)}
        />
      );
    }

    return (
      <PinEntry
        employee={selectedEmployee}
        companyName={companyConfig?.companyName}
        onBack={() => setSelectedEmployee(null)}
      />
    );
  }

  // Authenticated - show portal with tabs
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'schedule':
        // Salaried employees shouldn't reach here, but safeguard anyway
        return isSalaried ? <TimeOffScreen /> : <ScheduleScreen />;
      case 'timesheet':
        return isSalaried ? <TimeOffScreen /> : <TimesheetScreen />;
      case 'timeoff':
        return <TimeOffScreen />;
      case 'paystubs':
        return (
          <div className="flex items-center justify-center h-full text-slate-500">
            <p>Pay Stubs coming soon</p>
          </div>
        );
      default:
        return isSalaried ? <TimeOffScreen /> : <ScheduleScreen />;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-3">
          {logoMarkUrl && (
            <img src={logoMarkUrl} alt={platformName || companyConfig?.companyName} className="h-8 w-auto" />
          )}
          <div>
            <h1 className="text-lg font-semibold text-white">{companyConfig?.companyName}</h1>
            <p className="text-sm text-slate-400">
              {employee?.firstName} {employee?.lastName}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          className="px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
        >
          Sign Out
        </button>
      </header>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <main className="flex-1 overflow-y-auto">
        {renderActiveTab()}
      </main>
    </div>
  );
};

export default ClockRouter;
